import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {SeriesTypeBadge} from '@tickchak/react-share/app';
import * as css from './PromotionSummary.css';
import {formatRealtimeNumber, formatRealtimeDecimal} from '../../../functions';
import {MetricCard} from './MetricCard/MetricCard';
import {Money} from './Money/Money';
import PromotionHeader from './PromotionHeader/PromotionHeader';
import VisitsIcon from '../../../../../assets/icons/promotion/VisitsIcon';
import ConversionIcon from '../../../../../assets/icons/promotion/ConversionIcon';
import RevenueIcon from '../../../../../assets/icons/promotion/RevenueIcon';
import ProfitIcon from '../../../../../assets/icons/promotion/ProfitIcon';
import RoiIcon from '../../../../../assets/icons/promotion/RoiIcon';
import {promotionRocket, promotionRocketMobile} from '../../../../../assets/symbols/images/promotionRocket';

const PromotionSummary = () => {
    const {t} = useTranslation('realtime');
    const currency = useSelector((s: any) => s.event.currencySymbol);
    const darkDisplay = useSelector((s: any) => s.realtime.user.realtimeDesign?.darkDisplay);
    const {revenue, orders, visits, conversionRate, cost} = useSelector((s: any) => s.promotionSummary.event.stats);

    const visitsText = visits == null ? '—' : formatRealtimeNumber(visits, true);
    const conversionText = conversionRate == null ? '—' : `${formatRealtimeDecimal(conversionRate)}%`;
    const profit = revenue - cost;
    const roi = cost ? profit / cost : 0;
    const roiText = formatRealtimeDecimal(roi);
    const deductionPct = revenue ? Math.round((cost / revenue) * 100) : 0;

    return (
        <css.wrapper data-cy="promotion-summary">
            <css.headerRow>
                <PromotionHeader />
                <css.headerActions>
                    <css.headerContactRep />
                </css.headerActions>
            </css.headerRow>
            <css.grid>
                <MetricCard area="visits" title={t('pageVisits')} icon={<VisitsIcon color="currentColor" />} dataCy="promotion-visits"
                    value={visitsText}/>
                <MetricCard area="conversion" title={t('conversionRate')} icon={<ConversionIcon color="currentColor" />} dataCy="promotion-conversion"
                    value={conversionText}
                    subMetrics={[
                        {label: t('transactionsCount'), value: formatRealtimeNumber(orders, true)},
                        {label: t('pageVisits'), value: visitsText}
                    ]}/>
                <MetricCard area="revenue" title={t('promotionRevenue')} icon={<RevenueIcon color="currentColor" />} dataCy="promotion-revenue"
                    value={<Money amount={revenue} currency={currency}/>}/>
                <MetricCard area="profit" title={t('profit')} icon={<ProfitIcon color="currentColor" />} dataCy="promotion-profit"
                    badge={<css.badge backgroundColor="var(--primitive-gray-200)" color="var(--color-text-secondary)"
                        data-cy="promotion-deduction-badge">{t('deduction', {percent: deductionPct})}</css.badge>}
                    value={<Money amount={profit} currency={currency}/>}/>
                <MetricCard area="roi" title={t('roi')} icon={<RoiIcon color="currentColor" />} dataCy="promotion-roi" alignEnd
                    badge={profit > 0 && <SeriesTypeBadge planType="mixed" scheme={darkDisplay ? 'dark' : 'light'} label={t('profitable')} data-cy="promotion-profitable-badge" />}
                    value={t('roiMultiplier', {n: roiText})}
                    footer={t('roiSubtitle', {n: roiText})}
                    subMetrics={[
                        {label: t('attributedRevenue'), value: <Money amount={revenue} currency={currency}/>},
                        {label: t('promotionCost'), value: <Money amount={cost} currency={currency}/>}
                    ]}/>
                <css.rocketPicture>
                    <source media="(max-width: 800px)" srcSet={promotionRocketMobile} />
                    <css.rocketImg src={promotionRocket} alt="" />
                </css.rocketPicture>
            </css.grid>
            <css.mobileContactRep />
        </css.wrapper>
    );
};

export default PromotionSummary;
